import React from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { cx } from './cx';

/**
 * Growth Desk buttons. Orange is for the single next action (text on orange is navy, never
 * white); navy for confirm-type actions; ghost for everything else. 44px tall by default.
 */
export type DeskButtonVariant = 'primary' | 'navy' | 'ghost' | 'danger' | 'quiet' | 'plain';
export type DeskButtonSize = 'lg' | 'md' | 'sm' | 'xs';

const VARIANT: Record<DeskButtonVariant, string> = {
  primary: 'bg-brand text-brand-on hover:bg-brand-hover',
  navy: 'bg-navy text-on-navy hover:bg-navy-3',
  ghost: 'bg-transparent text-ink shadow-ring-line hover:shadow-ring-ink',
  danger: 'bg-bad text-card hover:opacity-90',
  quiet: 'bg-navy-3 text-on-navy hover:bg-navy-2',      // on navy panels
  plain: 'bg-transparent text-ink-2 hover:text-ink',
};
const SIZE: Record<DeskButtonSize, string> = {
  lg: 'min-h-[56px] px-5 text-base',
  md: 'min-h-[44px] px-[18px] text-sm',
  sm: 'min-h-[40px] px-3.5 text-sm',
  xs: 'min-h-[36px] px-3 text-[13px]',
};

export const deskButtonClass = (variant: DeskButtonVariant = 'ghost', size: DeskButtonSize = 'md', className?: string) => cx(
  'inline-flex items-center justify-center gap-2 rounded-btn font-display font-bold leading-none whitespace-nowrap no-underline',
  'transition-colors disabled:opacity-50 disabled:cursor-not-allowed aria-disabled:opacity-50 aria-disabled:pointer-events-none',
  VARIANT[variant], SIZE[size], className,
);

interface Common {
  variant?: DeskButtonVariant;
  size?: DeskButtonSize;
  loading?: boolean;
  icon?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
}

export const DeskButton: React.FC<Common & React.ButtonHTMLAttributes<HTMLButtonElement>> = ({
  variant = 'ghost', size = 'md', loading, icon, className, children, disabled, type = 'button', ...rest
}) => (
  <button type={type} className={deskButtonClass(variant, size, className)} disabled={disabled || loading} aria-busy={loading || undefined} {...rest}>
    {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : icon}
    {children}
  </button>
);

/** A button-styled in-app link (router) or, with `external`, a plain anchor. */
export const DeskLinkButton: React.FC<Common & { to: string; external?: boolean; title?: string }> = ({
  variant = 'ghost', size = 'md', icon, className, children, to, external, title,
}) => external
  ? <a href={to} target="_blank" rel="noreferrer" title={title} className={deskButtonClass(variant, size, className)}>{icon}{children}</a>
  : <Link to={to} title={title} className={deskButtonClass(variant, size, className)}>{icon}{children}</Link>;

/** An inline text link in the accent ink colour. */
export const TextLink: React.FC<{ to: string; children: React.ReactNode; className?: string; external?: boolean }> = ({ to, children, className, external }) => {
  const cls = cx('font-semibold text-brand-ink underline-offset-2 hover:underline', className);
  return external ? <a href={to} target="_blank" rel="noreferrer" className={cls}>{children}</a> : <Link to={to} className={cls}>{children}</Link>;
};
